// ============================================
// LOCAL STORAGE KEYS
// ============================================
export const STORAGE_KEYS = {
    USER_ID: 'myAppUserId',
    DEVICE_ID: 'myAppDeviceId',
    SESSION_ID: 'myAppSessionId',
    USER_TYPE: 'myAppUserType',
    LAST_PAGE: 'lastPage',
  } as const;
  
  // ============================================
  // ENVIRONMENT NAMES
  // ============================================
  export const ENVIRONMENTS = {
    LOCAL: 'Local',
    STAGING: 'Staging',
    QA: 'QA',
    DEVELOPMENT: 'Development',
    PRODUCTION: 'Production',
  } as const;
  
  // ============================================
  // EVENT CONFIGURATION
  // ============================================
  export const EVENT_TYPE = 'event';
  
  export const EVENT_ACTIONS = {
    BUTTON_CLICKED: 'button_clicked',
    SCROLL_DEPTH: 'scroll_depth',
    FORM_SUBMIT: 'form_submit',
    PAGE_VIEW: 'Page View',
  } as const;
  
  export const EVENT_CATEGORIES = {
    USER_ENGAGEMENT: 'User Engagement',
    FORM_INTERACTION: 'Form Interaction',
    HEADER_LINK: 'Header Link',
  } as const;
  
  export const DATA_LAYER_EVENTS = {
    BUTTON_CLICK: 'button_click',
    SCROLL_DEPTH: 'scroll_depth',
    FORM_SUBMIT: 'form_submit',
    PAGE_VIEW: 'page_view',
  } as const;
  
  // ============================================
  // MAGENTA CANVAS CONFIGURATION
  // ============================================
  export const CANVAS_CONFIG = {
    SOURCE: 'Magenta Canvas',
    TYPES: {
      COMPONENT: 'Component' as const,
      PAGE: 'Page' as const,
    },
    ACTIONS: {
      BUTTON_CLICKED: 'Button Clicked',
      PAGE_SCROLLED: 'Page Scrolled',
      PAGE_VIEW: 'Page View',
    },
  } as const;
  
  // ============================================
  // CHANNEL CONFIGURATION
  // ============================================
  export const CHANNEL = {
    WEB: 'Web',
    MOBILE: 'Mobile',
  } as const;
  
  // ============================================
  // DEVICE CONFIGURATION
  // ============================================
  export const DEVICE_CONFIG = {
    MOBILE_BREAKPOINT: 768,
    CATEGORIES: {
      MOBILE: 'mobile',
      DESKTOP: 'desktop',
    },
    LOCATION_ENABLED: 'False',
    DEFAULT_TIMEZONE: 'UTC',
  } as const;
  
  // ============================================
  // NETWORK CONFIGURATION
  // ============================================
  export const NETWORK_CONFIG = {
    UNKNOWN: 'unknown',
    EMPTY: '',
  } as const;
  
  // ============================================
  // SESSION CONFIGURATION
  // ============================================
  export const VISIT_TYPES = {
    NEW: 'new',
    RETURNING: 'returning',
  } as const;
  
  // ============================================
  // USER CONFIGURATION
  // ============================================
  export const USER_TYPES = {
    GUEST: 'guest',
  } as const;
  
  export const DEFAULT_LANGUAGE = 'EN-US';
  
  // ============================================
  // DEFAULT LABELS
  // ============================================
  export const DEFAULT_LABELS = {
    UNKNOWN: 'Unknown',
    SUBMIT: 'Submit',
    CONTACT_FORM: 'Contact us Form',
    JOIN_US: 'Join Us',
  } as const;

  
  // ============================================
  // HTML ATTRIBUTES
  // ============================================
  export const HTML_ATTRIBUTES = {
    ANALYTICS_LABEL: 'data-analytics-label',
    ORDER: 'data-order',
    BLOCK_NAME: 'data-blockName',
    BLOCK_NAME_LOWER: 'data-blockname',
    ARIA_LABEL: 'aria-label',
    ARIA_LABELLEDBY: 'aria-labelledby',
  } as const;
  
  // ============================================
  // CLICK TRACKING CONFIGURATION
  // ============================================
  export const CLICK_SELECTORS = 
    'button, [role="button"], a, input[type="button"], input[type="submit"], [data-analytics-label], input';
  
  export const NAVIGATION_DELAY_MS = 200;
  
  // ============================================
  // SCROLL TRACKING CONFIGURATION
  // ============================================
  export const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;
  
  // ============================================
  // DTDL HEADER LINK MAPPING
  // ============================================
  export const DTDL_HEADER_LINKS = {
    'Tech@DTDL': {
      label: 'DTDL Tech',
      category: EVENT_CATEGORIES.HEADER_LINK,
      position: '1',
      id: 'Header Link 1',
    },
    'Life@DTDL': {
      label: 'DTDL Life',
      category: EVENT_CATEGORIES.HEADER_LINK,
      position: '2',
      id: 'Header Link 2',
    },
    'Blogs & News': {
      label: 'DTDL Blog',
      category: EVENT_CATEGORIES.HEADER_LINK,
      position: '3',
      id: 'Header Link 3',
    },
    'JoinUs': {
      label: 'DTDL JoinUs',
      category: EVENT_CATEGORIES.HEADER_LINK,
      position: '4',
      id: 'Header Link 4',
    },
  } as const;
  
  // ============================================
  // MQTT CONFIGURATION
  // ============================================
  export const MQTT_CONFIG = {
    PROTOCOL: 'wss',
    PORT: 7443,
    WS_PATH: '/ws',
    RECONNECT_PERIOD: 1000, // Note: We override this in sendToRabbitMQ.ts
    CONNECT_TIMEOUT: 4000,
    PROTOCOL_VERSION: 4,
    CLEAN: true,
    MAX_QUEUE_SIZE: 100,
    MAX_RECONNECT_ATTEMPTS: 5,
    BASE_RECONNECT_DELAY: 1000,
    MAX_RECONNECT_DELAY: 30000, // 30 seconds max
    MESSAGE_BATCH_DELAY: 100, // Delay between messages when processing queue (ms)
    MESSAGE_BATCH_SIZE: 10, // Process up to 10 messages concurrently
    MAX_MESSAGE_RETRIES: 3,  } as const;
  
  export const MQTT_INSTANCE_TOPICS: Record<string, string> = {
    dtdl: 'dtdl.website',
    payzy: 'payzy.website',
  };
  
  // ============================================
  // API ENDPOINTS
  // ============================================
  export const API_ENDPOINTS = {
    GEO_LOCATION: 'https://ipwho.is/', // Free, unlimited, CORS-enabled, commercial use allowed
  } as const;
  
  // ============================================
  // DESCRIPTION TEMPLATES (Helper Functions)
  // ============================================
  export const DESCRIPTION_TEMPLATES = {
    buttonClicked: (label: string) => `User clicked the "${label}" button`,
    scrollDepth: (percent: number) => `User scrolled to ${percent}% of page`,
    formSubmitted: (formName: string) => `User submitted the ${formName}`,
    pageLanded: (pageTitle: string) => `User landed on ${pageTitle}`,
  } as const;